// GA4 Analytics component - displays Google Analytics 4 website data
// Shows website sessions, page views, form submissions, and top pages

/**
 * NOTES SUMMARY - GA4 ANALYTICS COMPONENT
 * 
 * What this component does:
 *  - Displays GA4 website analytics in card format
 *  - Shows 7-day and 30-day metrics
 *  - Includes sessions, page views, bounce rate, form submissions
 *  - Shows top pages and file downloads
 *  - Handles loading and error states
 *  - Uses Font Awesome icons
 * 
 * Props:
 *  - ga4Data: GA4 analytics object
 *  - loading: boolean
 *  - error: string
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faEye, faFileDownload, faFile, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../utils/constants';
import InfoTooltip from '../common/InfoTooltip';
import '../../styles/components.css';

function GA4Analytics() {
  const [ga4Data, setGA4Data] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('30');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);

  const loadGA4Data = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let params = '';
      if (selectedPreset === 'custom') {
        if (customStartDate && customEndDate) {
          params = `?startDate=${customStartDate}&endDate=${customEndDate}`;
        }
      } else {
        params = `?preset=${selectedPreset}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/get-ga4-data${params}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setGA4Data(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedPreset, customStartDate, customEndDate]);

  // Load GA4 data on component mount and when date filter changes
  useEffect(() => {
    if (selectedPreset !== 'custom') {
      loadGA4Data();
    }
  }, [selectedPreset, loadGA4Data]);

  // Load data on mount
  useEffect(() => {
    loadGA4Data();
  }, [loadGA4Data]);

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    setShowCustomRange(preset === 'custom');
  };

  const handleCustomRangeApply = () => {
    if (customStartDate && customEndDate) {
      loadGA4Data();
    }
  };

  const formatDateRange = () => {
    if (!ga4Data?.dateRange) return '';
    const { startDate, endDate } = ga4Data.dateRange;
    const start = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const end = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${start} - ${end}`;
  };
  
  if (loading) {
    return (
      <div className="ga4-analytics-card">
        <div className="ga4-analytics-header">
          <FontAwesomeIcon icon={faChartLine} className="ga4-icon" />
          <h3>Website Analytics</h3>
        </div>
        <div className="ga4-loading">Loading website data...</div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="ga4-analytics-card">
        <div className="ga4-analytics-header">
          <FontAwesomeIcon icon={faChartLine} className="ga4-icon" />
          <h3>Website Analytics</h3>
        </div>
        <div className="ga4-error">
          Failed to load website data: {error}
        </div>
      </div>
    );
  }
  
  if (!ga4Data) {
    return (
      <div className="ga4-analytics-card">
        <div className="ga4-analytics-header">
          <FontAwesomeIcon icon={faChartLine} className="ga4-icon" />
          <h3>Website Analytics</h3>
        </div>
        <div className="ga4-empty">No website data available</div>
      </div>
    );
  }
  
  // Format numbers with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return new Intl.NumberFormat().format(num);
  };
  
  // Format duration (seconds to minutes)
  const formatDuration = (seconds) => {
    if (!seconds) return '0m 0s';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };
  
  // Format percentage
  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${Math.round(value * 100)}%`;
  };
  
  const {
    sessions,
    pageViews,
    bounceRate,
    avgSessionDuration,
    formSubmissions,
    fileDownloads,
    topPages = [],
    lastUpdated
  } = ga4Data;
  
  return (
    <div className="ga4-analytics-card">
      <div className="ga4-analytics-header">
        <FontAwesomeIcon icon={faChartLine} className="ga4-icon" />
        <h3>Website Analytics</h3>
        <div className="ga4-header-controls">
          <div className="ga4-date-filter">
            <FontAwesomeIcon icon={faCalendarAlt} className="ga4-filter-icon" />
            <select 
              value={selectedPreset} 
              onChange={(e) => handlePresetChange(e.target.value)}
              className="ga4-date-select"
            >
              <option value="7">7 Days</option>
              <option value="30">30 Days</option>
              <option value="90">90 Days</option>
              <option value="365">365 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          {lastUpdated && (
            <span className="ga4-last-updated">
              Updated: {new Date(lastUpdated).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
      
      {showCustomRange && (
        <div className="ga4-custom-range">
          <div className="ga4-date-inputs">
            <div className="ga4-date-input">
              <label>Start Date:</label>
              <input 
                type="date" 
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
            </div>
            <div className="ga4-date-input">
              <label>End Date:</label>
              <input 
                type="date" 
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </div>
            <button 
              onClick={handleCustomRangeApply}
              disabled={!customStartDate || !customEndDate}
              className="ga4-apply-btn"
            >
              Apply
            </button>
          </div>
        </div>
      )}
      
      {ga4Data?.dateRange && (
        <div className="ga4-date-range-display">
          <span className="ga4-date-range-text">{formatDateRange()}</span>
        </div>
      )}
      
      {/* Key Metrics Grid */}
      <div className="ga4-metrics-grid">
        <div className="ga4-metric-item">
          <div className="ga4-metric-header">
            <FontAwesomeIcon icon={faEye} className="ga4-metric-icon" />
            <span>Sessions</span>
            <InfoTooltip text="A session is a single visit to your website. One visitor can have multiple sessions." position="bottom" />
          </div>
          <div className="ga4-metric-value-single">
            <span className="ga4-single-value">{formatNumber(sessions)}</span>
          </div>
        </div>
        
        <div className="ga4-metric-item">
          <div className="ga4-metric-header">
            <FontAwesomeIcon icon={faChartLine} className="ga4-metric-icon" />
            <span>Page Views</span>
            <InfoTooltip text="Total number of pages viewed across all sessions. Includes repeat views of the same page." position="bottom" />
          </div>
          <div className="ga4-metric-value-single">
            <span className="ga4-single-value">{formatNumber(pageViews)}</span>
          </div>
        </div>
        
        <div className="ga4-metric-item">
          <div className="ga4-metric-header">
            <FontAwesomeIcon icon={faFile} className="ga4-metric-icon" />
            <span>Form Submissions</span>
          </div>
          <div className="ga4-metric-value-single">
            <span className="ga4-single-value">{formatNumber(formSubmissions)}</span>
          </div>
        </div>
        
        <div className="ga4-metric-item">
          <div className="ga4-metric-header">
            <FontAwesomeIcon icon={faFileDownload} className="ga4-metric-icon" />
            <span>Downloads</span>
          </div>
          <div className="ga4-metric-value-single">
            <span className="ga4-single-value">{formatNumber(fileDownloads)}</span>
          </div>
        </div>
      </div>
      
      {/* Additional Metrics Row */}
      <div className="ga4-additional-metrics">
        <div className="ga4-additional-metric">
          <span className="ga4-additional-label">Bounce Rate</span>
          <span className="ga4-additional-value">{formatPercentage(bounceRate)}</span>
        </div>
        <div className="ga4-additional-metric">
          <span className="ga4-additional-label">Avg Session Duration</span>
          <span className="ga4-additional-value">{formatDuration(avgSessionDuration)}</span>
        </div>
      </div>
      
      {/* Top Pages Section */}
      {topPages && topPages.length > 0 && (
        <div className="ga4-top-pages">
          <h4 className="ga4-section-title">Top Pages</h4>
          <div className="ga4-pages-list">
            {topPages.slice(0, 5).map((page, index) => (
              <div key={index} className="ga4-page-item">
                <div className="ga4-page-info">
                  <span className="ga4-page-title">{page.title || page.path}</span>
                  <span className="ga4-page-path">{page.path}</span>
                </div>
                <span className="ga4-page-views">{formatNumber(page.views)} views</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default GA4Analytics;